import React, { useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

interface MessageBubbleProps {
  content: string;
  messageId: string;
  searchQuery: string;
  currentMatchIndex: number;
}

const renderHighlightedText = (
  text: string,
  searchQuery: string,
  currentMatchIndex: number,
  matchOrdinalRef: { current: number },
  matchRefs: { current: Record<number, HTMLElement | null> }
) => {
  if (!searchQuery.trim()) return text;
  const q = searchQuery.toLowerCase();
  const escaped = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) => {
    if (part.length > 0 && part.toLowerCase() === q) {
      const ordinal = matchOrdinalRef.current++;
      const isCur = ordinal === currentMatchIndex;
      return (
        <span
          key={i}
          ref={el => { matchRefs.current[ordinal] = el; }}
          className={`bx-highlight${isCur ? ' bx-highlight--current' : ''}`}
        >
          {part}
        </span>
      );
    }
    return part;
  });
};

const highlightIn = (
  children: React.ReactNode,
  searchQuery: string,
  currentMatchIndex: number,
  matchOrdinalRef: { current: number },
  matchRefs: { current: Record<number, HTMLElement | null> }
) =>
  React.Children.map(children, c =>
    typeof c === 'string'
      ? renderHighlightedText(c, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)
      : c
  );

const sanitizeMarkdown = (text: string): string => {
  if (!text) return text;
  let out = text.replace(
    /<code\s+block=["']?([^"'\s>]*)["']?>([\s\S]*?)<\/code>/gi,
    (_: string, lang: string, body: string) => `\n\`\`\`${lang || ''}\n${body.trim()}\n\`\`\`\n`,
  );
  out = out.replace(/<code>([\s\S]*?)<\/code>/gi, (_: string, body: string) => `\`${body.trim()}\``);
  return out;
};

const getMdComponents = (
  searchQuery: string,
  currentMatchIndex: number,
  matchOrdinalRef: { current: number },
  matchRefs: { current: Record<number, HTMLElement | null> }
) => ({
  code({ value, node, inline, ...props }: any) {
    if (!inline && value) {
      const highlight = (code: string) => {
        return code
          .replace(/(["'`])(.*?)\1/g, '<span style="color:#4ade80">$1$2$1</span>')
          .replace(/\b(\d+)\b/g, '<span style="color:#60a5fa">$1</span>')
          .replace(/(\/\/.*$|\/\*[\s\S]*?\*\/)/gm, '<span style="color:#6b7280;font-style:italic">$1</span>')
          .replace(/\b(const|let|var|function|return|if|else|for|while|class|import|export|from|async|await|new|true|false|null|undefined)\b/g,
            '<span style="color:#c084fc">$1</span>')
          .replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(?=\()/g,
            '<span style="color:#ef7c4d">$1</span>');
      };

      return (
        <pre style={{
          background: 'rgba(0,0,0,0.6)',
          borderRadius: '8px',
          padding: '16px',
          overflowX: 'auto',
          margin: '8px 0',
          border: '1px solid rgba(255,255,255,0.05)',
        }}>
          <code
            dangerouslySetInnerHTML={{ __html: highlight(value) }}
            style={{
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              fontSize: '12px',
              lineHeight: '1.6',
              display: 'block',
            }}
          />
        </pre>
      );
    }
    return (
      <code style={{
        background: 'rgba(0,0,0,0.3)',
        padding: '2px 6px',
        borderRadius: '4px',
        fontFamily: 'ui-monospace, monospace',
        fontSize: '12px',
        color: 'var(--bx-brand)',
      }}>
        {value}
      </code>
    );
  },
  p({ children }: any) { return <p style={{ marginBottom: '8px', wordBreak: 'break-word' }}>{highlightIn(children, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)}</p>; },
  li({ children }: any) { return <li style={{ marginBottom: '4px' }}>{highlightIn(children, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)}</li>; },
  h1({ children }: any) { return <h1 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '16px' }}>{highlightIn(children, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)}</h1>; },
  h2({ children }: any) { return <h2 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>{highlightIn(children, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)}</h2>; },
  h3({ children }: any) { return <h3 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>{highlightIn(children, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)}</h3>; },
  strong({ children }: any) { return <strong style={{ fontWeight: 700 }}>{highlightIn(children, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)}</strong>; },
  em({ children }: any) { return <em style={{ fontStyle: 'italic' }}>{highlightIn(children, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)}</em>; },
  blockquote({ children }: any) { return <blockquote style={{ borderLeft: '2px solid rgba(255,109,90,0.5)', paddingLeft: '16px', fontStyle: 'italic', margin: '8px 0' }}>{highlightIn(children, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)}</blockquote>; },
  td({ children }: any) { return <td style={{ border: '1px solid rgba(255,255,255,0.1)', padding: '8px' }}>{highlightIn(children, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)}</td>; },
  th({ children }: any) { return <th style={{ border: '1px solid rgba(255,255,255,0.1)', padding: '8px', fontWeight: 700 }}>{highlightIn(children, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)}</th>; },
  text({ value }: any) { return <>{renderHighlightedText(value, searchQuery, currentMatchIndex, matchOrdinalRef, matchRefs)}</>; },
});

function MessageBubble({ content, messageId, searchQuery, currentMatchIndex }: MessageBubbleProps) {
  const matchOrdinalRef = useRef({ current: 0 });
  const matchRefs = useRef({ current: {} as Record<number, HTMLElement | null> });
  
  matchOrdinalRef.current.current = 0; // Reset on render

  const sanitized = useMemo(() => sanitizeMarkdown(content), [content]);
  
  const components = useMemo(() => 
    getMdComponents(searchQuery, currentMatchIndex, matchOrdinalRef.current, matchRefs.current), 
  [searchQuery, currentMatchIndex]);

  return (
    <ReactMarkdown renderers={components}>{sanitized}</ReactMarkdown>
  );
}

export default React.memo(MessageBubble);
